import { useEffect, useState, useCallback } from "react";
import api, { downloadPdf } from "@/lib/apiClient";
import { currentQuarter, quarterOptions, quarterLabel } from "@/lib/periods";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Award, Download, Medal, Target, FileDown } from "lucide-react";
import { toast } from "sonner";

export default function Certificates() {
  const [quarter, setQuarter] = useState(currentQuarter());
  const [threshold, setThreshold] = useState(75);
  const [data, setData] = useState(null);
  const [batching, setBatching] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setThreshold(r.data.attendance_goal)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get("/certificates", { params: { quarter, threshold } }).then((r) => setData(r.data)).catch(() => {});
  }, [quarter, threshold]);

  useEffect(() => { load(); }, [load]);

  const gen = async (m) => {
    try {
      await downloadPdf("/certificates/pdf", { member_id: m.member_id, quarter }, `certificado-${m.name.replace(/\s/g, "_")}.pdf`);
      toast.success(`Certificado de ${m.name} gerado!`);
    } catch {
      toast.error("Erro ao gerar certificado");
    }
  };

  const genAll = async () => {
    setBatching(true);
    try {
      await downloadPdf("/certificates/pdf/all", { quarter, threshold }, `certificados-${quarter}.pdf`);
      toast.success("Todos os certificados gerados em um único PDF!");
    } catch {
      toast.error("Nenhum membro elegível para gerar.");
    }
    setBatching(false);
  };

  return (
    <div className="space-y-8 animate-fade-up" data-testid="certificates-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Certificados de Participação</h1>
          <p className="text-muted-foreground mt-1">Membros assíduos elegíveis no trimestre, conforme a meta definida.</p>
        </div>
        <div>
          <Label className="text-xs">Trimestre</Label>
          <Select value={quarter} onValueChange={setQuarter}>
            <SelectTrigger className="mt-1.5 w-48" data-testid="certificate-quarter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {quarterOptions().map((q) => <SelectItem key={q} value={q}>{quarterLabel(q)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="p-6" data-testid="certificate-threshold-control">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center"><Target className="h-5 w-5" /></span>
            <div>
              <div className="font-display font-semibold">Meta de assiduidade</div>
              <div className="text-sm text-muted-foreground">Frequência mínima para receber certificado</div>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-1 max-w-md min-w-[240px]">
            <Slider value={[threshold]} onValueChange={(v) => setThreshold(v[0])} min={50} max={100} step={5} data-testid="certificate-threshold-slider" />
            <Badge className="bg-accent/15 text-accent border-none text-base px-3 py-1 shrink-0">{threshold}%</Badge>
          </div>
        </div>
      </Card>

      {data && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Medal className="h-5 w-5 text-accent" /> Elegíveis</h3>
            <div className="flex items-center gap-3">
              <Badge variant="secondary">{data.eligible.length} de {data.total_evaluated} membros</Badge>
              <Button size="sm" onClick={genAll} disabled={batching || data.eligible.length === 0} className="gap-2" data-testid="download-all-certificates-button">
                <FileDown className="h-4 w-4" /> {batching ? "Gerando…" : "Baixar todos"}
              </Button>
            </div>
          </div>
          {data.eligible.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">Nenhum membro atingiu {threshold}% de frequência neste trimestre.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="certificate-eligible-list">
              {data.eligible.map((m) => (
                <div key={m.member_id} className="rounded-xl border border-border p-4 flex flex-col gap-3" data-testid="certificate-member-card">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold">{m.name}</div>
                      <div className="text-xs text-muted-foreground">{m.group_name}</div>
                    </div>
                    <Award className="h-5 w-5 text-accent" />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Badge className="bg-[hsl(var(--emerald))]/15 text-[hsl(var(--emerald))] border-none">{m.rate}%</Badge>
                    {m.present}/{m.meetings} encontros
                  </div>
                  <Button size="sm" variant="outline" className="gap-2" onClick={() => gen(m)} data-testid={`generate-certificate-${m.member_id}`}>
                    <Download className="h-4 w-4" /> Gerar certificado
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
